import styled, { css } from "styled-components";
import { Option } from "@cs/components/src/Select";
import React from "react";
import useJobProgress, { formatElapsed } from "../../hooks/useJobProgress";

/** Soft background + colored border/text, matching Spotter's own chip pattern (\`TagChip\`/
 *  \`GlossaryChip\`/\`TrayChip\`) instead of an opaque solid fill. */
const Badge = styled.div<{ $status: string }>`
  border-radius: 7px;
  height: 25px;
  padding: 0 10px;
  background-color: var(--blueBackground);
  border: 1px solid var(--blueBorder);
  color: var(--blue400);
  display: inline-flex;
  align-items: center;

  ${(props) =>
    (props.$status === "error" || props.$status === "aborted") &&
    css`
      background-color: var(--redBackground);
      border-color: var(--redBorder);
      color: var(--red400);
    `};

  ${(props) =>
    props.$status === "success" &&
    css`
      background-color: var(--greenBackground);
      border-color: var(--greenBorder);
      color: var(--green400);
    `};

  ${(props) =>
    (props.$status === "retrying" || props.$status === "warning") &&
    css`
      background-color: var(--orangeBackground);
      border-color: var(--orangeBorder);
      color: var(--orange400);
    `};
`;

const StyledDate = styled.span`
  color: var(--dark700);
  padding-right: 10px;
`;

export const useScriptExecutionValues = (
  inputScript?: ScriptExecution | ScriptExecutionStore,
  organizations?: Option[]
):
  | Record<
      string,
      {
        label: string;
        value: React.ReactNode;
        multiline?: boolean;
      }
    >
  | undefined => {
  const scriptExecution =
    inputScript && "scriptExecution" in inputScript
      ? inputScript.scriptExecution
      : inputScript;
  const script =
    inputScript && "scriptExecution" in inputScript ? inputScript : undefined;
  const jobProgress = useJobProgress(scriptExecution ?? {});

  if (!scriptExecution) {
    return undefined;
  }

  const formatDate = (date: string | number) =>
    new Date(date).toLocaleString("en-US", {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });

  let statusText = "Unknown status";
  const status = scriptExecution.status;
  if (status === "waiting") {
    statusText = "Waiting";
  } else if (status === "error") {
    statusText = "Error";
  } else if (status === "success") {
    statusText = "Success";
  } else if (status === "running") {
    statusText = "Running";
  } else if (status === "retrying") {
    statusText = "Retrying";
  } else if (status === "warning") {
    statusText = "Warning";
  } else if (status === "aborted") {
    statusText = "Aborted";
  }

  return {
    systemDomainId: {
      label: "Organization",
      value: organizations?.find(
        ({ value }) => value === scriptExecution.systemDomainId
      )?.label,
    },
    title: { label: "Title", value: scriptExecution.title },
    user: { label: "User", value: scriptExecution.userName },
    status: {
      label: "Status",
      value: <Badge $status={status}>{statusText}</Badge>,
    },
    receivedDate: {
      label: "Received date",
      value: scriptExecution.receivedDate
        ? formatDate(scriptExecution.receivedDate)
        : "",
    },
    waitingTime: {
      label: "Waiting time",
      value:
        scriptExecution.startDate && scriptExecution.receivedDate
          ? formatElapsed(
              (new Date(scriptExecution.startDate).valueOf() -
                new Date(scriptExecution.receivedDate).valueOf()) /
                1000
            )
          : "",
    },
    startDate: {
      label: "Start date",
      value: scriptExecution.startDate
        ? formatDate(scriptExecution.startDate)
        : "",
    },
    progress: jobProgress,
    script: { label: "Script", value: script?.scriptKey },
    id: { label: "ID", value: scriptExecution.id },
    requestId: {
      label: "Lambda requests",
      value: scriptExecution.requestIds?.join("\n"),
      multiline: true,
    },
    messages: {
      label: "Messages",
      value:
        scriptExecution.messages?.length > 0
          ? scriptExecution.messages.map(({ message, date }) => (
              <div key={date + message}>
                <StyledDate>{formatDate(date)}</StyledDate>
                <span>{message}</span>
              </div>
            ))
          : undefined,
      multiline: true,
    },
    variables: {
      label: "Variables",
      value: JSON.stringify(script?.variables, null, 2),
      multiline: true,
    },
    body: {
      label: "Body",
      value: JSON.stringify(script?.body, null, 2),
      multiline: true,
    },
  };
};
